/**
 * ConnectionSetupAffordance — connector-status-truth (bug #2)
 *
 * The MCP list card used to render only a StatusDot + tier badge + stats.
 * When the backend reports a connector is added-but-not-usable
 * (`connectionState !== 'connected'`), the user had no way to tell WHY
 * (sign-in needed? revoked? never tested?) and no way to DO anything
 * about it. The connector-status-truth fix downgrades the dot to amber/red,
 * but per the founder's "UI shows a status the backend can't back up"
 * rule the card must also surface the *action*.
 *
 * This component renders, from the backend's `needsSetupReason`:
 *   - a small "Sign in to use" / "Setup required" / "Unavailable" badge, and
 *   - an actionable button that routes to the connection's setup surface
 *     (the detail/Configure page, where credentials + Test live) — or, for
 *     `never_tested` / `degraded`, fires the inline Test action passed by
 *     the parent.
 *
 * It renders NOTHING when the connector is genuinely usable
 * (`needsSetupReason === 'ok'` / undefined), so healthy cards are unchanged.
 *
 * Truth-over-speed: there is no fake "signed in!" toast. For OAuth servers
 * the button routes to where credentials are actually configured; it never
 * claims a sign-in succeeded.
 */

import { LogIn, AlertTriangle, Ban, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { McpNeedsSetupReason } from '@/lib/mock-connections-data';

export interface ConnectionSetupAffordanceProps {
  /** Backend's real-usability reason. `ok`/undefined → renders nothing. */
  needsSetupReason?: McpNeedsSetupReason;
  /** True when the system server declares OAuth/token auth. */
  requiresAuth?: boolean;
  /** True when an OAuth token is present. */
  signedIn?: boolean;
  /** Route the user to the setup/credentials surface (detail page). */
  onSetup: () => void;
  /** Fire the inline Test action (for never_tested / degraded). */
  onTest?: () => void;
  /** Disable the Test button while a test is in flight. */
  testing?: boolean;
  className?: string;
}

type AffordanceSpec = {
  label: string;
  tone: 'amber' | 'red';
  icon: typeof LogIn;
  /** Which action the primary button performs. */
  action: 'setup' | 'test';
  /** Button label. */
  cta: string;
};

const TONE_STYLES: Record<AffordanceSpec['tone'], string> = {
  amber:
    'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  red: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

/**
 * Map the backend reason → user-facing affordance. Pure + exported so a
 * unit test can assert the full reason matrix without rendering.
 */
export function resolveSetupAffordance(
  reason: McpNeedsSetupReason | undefined,
): AffordanceSpec | null {
  switch (reason) {
    case 'needs_signin':
      return { label: 'Sign in to use', tone: 'amber', icon: LogIn, action: 'setup', cta: 'Sign in' };
    case 'token_expired':
      return { label: 'Session expired', tone: 'amber', icon: LogIn, action: 'setup', cta: 'Sign in again' };
    case 'never_tested':
      return { label: 'Setup required', tone: 'amber', icon: AlertTriangle, action: 'test', cta: 'Test connection' };
    case 'degraded':
      return { label: 'Degraded', tone: 'amber', icon: AlertTriangle, action: 'test', cta: 'Re-test' };
    case 'revoked':
      return { label: 'Revoked', tone: 'red', icon: Ban, action: 'setup', cta: 'Review' };
    case 'inactive':
      return { label: 'Unavailable', tone: 'red', icon: Ban, action: 'setup', cta: 'Review' };
    case 'ok':
    case undefined:
    default:
      return null;
  }
}

export function ConnectionSetupAffordance({
  needsSetupReason,
  onSetup,
  onTest,
  testing,
  className,
}: ConnectionSetupAffordanceProps) {
  const spec = resolveSetupAffordance(needsSetupReason);
  if (!spec) return null;

  const Icon = spec.icon;
  const runTest = spec.action === 'test' && typeof onTest === 'function';

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-md border border-dashed px-2.5 py-1.5',
        TONE_STYLES[spec.tone],
        className,
      )}
      data-testid="mcp-setup-affordance"
      data-reason={needsSetupReason}
    >
      <Badge
        variant="outline"
        className={cn('gap-1 text-[10px]', TONE_STYLES[spec.tone])}
      >
        <Icon className="h-3 w-3" />
        {spec.label}
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-[10px]"
        disabled={runTest && testing}
        onClick={(e) => {
          e.stopPropagation();
          if (runTest) onTest?.();
          else onSetup();
        }}
      >
        {runTest && testing ? (
          <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
        ) : null}
        {spec.cta}
      </Button>
    </div>
  );
}
